import type { VoiceServerEvent } from '@pointfive/contracts';

/** The client half of the voice relay (CMP-VOICE-CLIENT, FLOW-VOICE, SDD §20.2).
 *
 *  Opens a socket to *our* relay with a 60-second device-bound ticket. Not to a provider — the client never holds a
 *  provider credential, which is the whole reason the relay exists. The ticket is single-use, so a connection is
 *  preceded by a `voice.session.ticket` call every time; reconnecting is not free and is not meant to be.
 *
 *  The socket carries a turn, not a stream of audio. Audio never leaves the browser in this build (there is no ASR
 *  credential — see `transcribe.ts`), so what goes up is the finished utterance and what comes back is the answer in
 *  chunks. When a live ASR exists the frames change and this file changes with them; nothing above it does, because
 *  everything above it speaks in machine events.
 *
 *  Barge-in is sent as a message rather than a disconnect, matching the server: interrupting must not cost a
 *  reconnection, and a new ticket is a round-trip the trader would feel.
 */

/** The wire protocol comes from `@pointfive/contracts`, the same definition the relay validates against. Declaring
 *  it here as well is how the two ends drifted the first time. */
export type RelayServerEvent = VoiceServerEvent;

export interface RelaySocket {
  send(data: string): void;
  close(): void;
  readonly readyState: number;
}

export interface RelayHandlers {
  onEvent(event: RelayServerEvent): void;
  /** The socket went away — dropped, expired, refused. The HUD stops waiting rather than sitting in `thinking`. */
  onClosed(reason: string): void;
}

export interface RelayOptions extends RelayHandlers {
  /** Injectable so the transport is testable without a server, and swappable if the URL scheme ever changes. */
  readonly openSocket?: (url: string) => RelaySocket & {
    addEventListener(type: string, listener: (event: never) => void): void;
  };
}

export interface RelayConnection {
  /** Sends a finished utterance. Every turn carries a fresh idempotency key: a key identifies a request, not its
   *  content, and a conversation is exactly where somebody asks the same thing twice. */
  utter(text: string, idempotencyKey: string): void;
  /** Barge-in. The turn is abandoned; the socket and the transcript stay. */
  interrupt(): void;
  close(): void;
  readonly open: boolean;
}

export interface RelayTicket {
  readonly ticket: string;
  readonly relayUrl: string;
  readonly deviceId: string;
}

function browserSocket(url: string) {
  const socket = new WebSocket(url);
  return socket as unknown as RelaySocket & {
    addEventListener(type: string, listener: (event: never) => void): void;
  };
}

/** Connects and resolves once the relay has said `ready` — which carries the lane and the minutes, so the HUD knows
 *  whether it will be spoken to before the first turn rather than finding out by not being. */
export async function connectRelay(ticket: RelayTicket, options: RelayOptions): Promise<RelayConnection> {
  const url = `${ticket.relayUrl}?ticket=${encodeURIComponent(ticket.ticket)}&device=${encodeURIComponent(ticket.deviceId)}`;
  const socket = (options.openSocket ?? browserSocket)(url);
  let closed = false;

  socket.addEventListener('message', ((event: { data: unknown }) => {
    let parsed: RelayServerEvent;
    try {
      parsed = JSON.parse(String(event.data)) as RelayServerEvent;
    } catch {
      // A frame we cannot read is not a reason to drop a working session; the turn that needed it will time out.
      return;
    }
    options.onEvent(parsed);
  }) as never);

  socket.addEventListener('close', (() => {
    closed = true;
    options.onClosed('The voice connection closed.');
  }) as never);

  socket.addEventListener('error', (() => {
    // `error` is always followed by `close`, so the reason is recorded here and the state change happens there.
    options.onClosed('The voice connection failed.');
  }) as never);

  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('The voice relay did not answer.')), 10_000);
    socket.addEventListener('open', (() => {
      clearTimeout(timer);
      resolve();
    }) as never);
    socket.addEventListener('close', (() => {
      clearTimeout(timer);
      // Rejecting on close covers the refusals: an expired ticket, the wrong device, another origin. All three come
      // back as a failed handshake rather than a message, because a refused upgrade has nowhere to put one.
      reject(new Error('The voice relay refused the connection.'));
    }) as never);
  });

  const send = (payload: unknown): void => {
    if (!closed) socket.send(JSON.stringify(payload));
  };

  return {
    utter: (text, idempotencyKey) => send({ type: 'utterance', text, idempotencyKey }),
    interrupt: () => send({ type: 'interrupt' }),
    close: () => {
      if (closed) return;
      send({ type: 'bye' });
      socket.close();
    },
    get open() {
      return !closed;
    },
  };
}
